import React from "react";

import SearchIcon from "@mui/icons-material/Search";
import {
  alpha,
  Box,
  FormControl,
  InputAdornment,
  MenuItem,
  Select,
  Stack,
  TextField,
  type Theme,
} from "@mui/material";

export type StatusFilter = "all" | "active" | "setting_up" | "archived";
export type TeamFilter = "all" | string;
export type SortOption = "recent" | "name";

interface ProjectsToolbarProps {
  search: string;
  onSearchChange: (value: string) => void;
  status: StatusFilter;
  onStatusChange: (value: StatusFilter) => void;
  team: TeamFilter;
  onTeamChange: (value: TeamFilter) => void;
  sort: SortOption;
  onSortChange: (value: SortOption) => void;
  teams?: string[];
}

const selectSx = (theme: Theme) => ({
  minWidth: 120,
  backgroundColor: alpha(theme.palette.background.paper, 0.6),
  "& .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.divider, 0.08),
  },
  "&:hover .MuiOutlinedInput-notchedOutline": {
    borderColor: alpha(theme.palette.divider, 0.16),
  },
});

export const ProjectsToolbar: React.FC<ProjectsToolbarProps> = ({
  search,
  onSearchChange,
  status,
  onStatusChange,
  team,
  onTeamChange,
  sort,
  onSortChange,
  teams = [],
}) => {
  return (
    <Box
      sx={(theme) => ({
        display: "flex",
        alignItems: "center",
        gap: 2,
        flexWrap: "wrap",
        px: 2.5,
        py: 1.5,
        borderRadius: 2,
        bgcolor: "background.paper",
        border: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
        boxShadow: `0 2px 8px ${alpha(theme.palette.common.black, 0.04)}`,
      })}
    >
      <TextField
        placeholder="Search projects…"
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        size="small"
        sx={(theme) => ({
          flex: "1 1 260px",
          minWidth: 220,
          backgroundColor: alpha(theme.palette.background.paper, 0.6),
          "& .MuiOutlinedInput-root": {
            borderRadius: 1.5,
            "& fieldset": { borderColor: alpha(theme.palette.divider, 0.08) },
            "&:hover fieldset": {
              borderColor: alpha(theme.palette.divider, 0.16),
            },
          },
        })}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon fontSize="small" color="disabled" />
            </InputAdornment>
          ),
        }}
      />
      <Stack direction="row" spacing={1.5} sx={{ flexWrap: "wrap" }}>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={status}
            onChange={(e) => onStatusChange(e.target.value as StatusFilter)}
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="all">All statuses</MenuItem>
            <MenuItem value="active">Active</MenuItem>
            <MenuItem value="setting_up">Setting up</MenuItem>
            <MenuItem value="archived">Archived</MenuItem>
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 120 }}>
          <Select
            value={team}
            onChange={(e) => onTeamChange(e.target.value as TeamFilter)}
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="all">All teams</MenuItem>
            {teams.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 130 }}>
          <Select
            value={sort}
            onChange={(e) => onSortChange(e.target.value as SortOption)}
            displayEmpty
            sx={selectSx}
          >
            <MenuItem value="recent">Sort: recent</MenuItem>
            <MenuItem value="name">Sort: name</MenuItem>
          </Select>
        </FormControl>
      </Stack>
    </Box>
  );
};

export default ProjectsToolbar;
